import { Loader, ShoppingCart } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { can_visit, getPractice, notify } from '../../../helpers';
import { useDocumentTitle } from '../../../hooks/customDocumentTitleHook';
import { Config } from '../../../Config';
import { useLocation } from 'react-router-dom';
import { oneTimeCheckoutPackage } from '../../../services/billing';
import Spacing from '../../Spacing';
import StatusViewModal from '../../Modal/StatusViewModal';
import FormViewDetailsModal from '../../Modal/FormViewDetailsModal';

export default function LetterCredits({selectedPracticeChange, fetchChangePracticeData, letterLimit}) {
  let account_id = getPractice("account_id");
  if(selectedPracticeChange != null && fetchChangePracticeData){
    account_id = selectedPracticeChange?.account_id;
  }

  const letterQuantiity = Config.LetterCreditQuantity;
  const [paymentStatus, setPaymentStatus] = useState(false);
  const [hasCheckedStatus, setHasCheckedStatus] = useState(false);
  const [formViewId, setFormViewId] = useState(null);
  const [formView, setFormView] = useState(false);
  const [loading, setLoading] = useState(false);
  const [informations, setInformations] = useState({
    account_id: account_id,
    available_credit: 0,
    quantity: 0,
    type: 'letter_credits'
  });

  const [previousScrollPosition, setPreviousScrollPosition] = useState(0);
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const subscriptionSession = queryParams.get('session_id');
  const subscriptionPaymentStatus = queryParams.get('payment');
  useEffect(() => {
    if (subscriptionSession !== null && !hasCheckedStatus) {
      setPaymentStatus(true);
      setHasCheckedStatus(true);
      setPreviousScrollPosition(window.scrollY);
      window.scrollTo(0, 0);
    }
  }, [subscriptionSession, hasCheckedStatus]);

  const handleFormView = (formId) => {
    setFormViewId(formId)
    setFormView(true);
    setPreviousScrollPosition(window.scrollY);
    window.scrollTo(0, 0);
  };

  const substrateCredit = () => {
    setInformations((prevState) => {
      const newCredit = Math.max(prevState.available_credit - letterQuantiity, 0);
      const newQuantity = Math.floor(newCredit / letterQuantiity);
      return { ...prevState, available_credit: newCredit, quantity: newQuantity };
    });
  };

  const addMoreCredit = () => {
    setInformations((prevState) => {
      const newCredit = prevState.available_credit + letterQuantiity;
      const newQuantity = Math.floor(newCredit / letterQuantiity);
      return { ...prevState, available_credit: newCredit, quantity: newQuantity };
    });
  };
  const handleSubmit = async (e) => {
    e.preventDefault();
    e.target.disabled = true;
    setLoading(true);

    const { available_credit, ...buyCredit } = informations;

    try {
      const result = await oneTimeCheckoutPackage(buyCredit);

      if(result){
        if(result?.data?.payment_status === "unpaid" && result?.data?.status === "open"){
          window.location.href = result?.data?.url;
        } else {
          notify("error", result?.detail);
        }
      }
    } catch (error) {
      if (error?.response && error?.response?.data && error?.response?.data?.detail) {
        notify("error", error?.response?.data?.detail, 0.5);
      }
    } finally {
      setInformations({
        ...informations,
        available_credit: 0,
        quantity: 0,
      });
      setLoading(false);
      e.target.disabled = false;
    }
  };

  useEffect(() => {
    setInformations({
      ...informations,
      account_id: account_id,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account_id]);

  const totalCost = (informations.available_credit / letterQuantiity) * 10;
  
  useDocumentTitle("Letter Credits");

  return (
    <>
      <h3 className="docpro_fs_24 docpro_mb_0">Buy Letter Credits</h3>
      <Spacing lg="22" sm="22" />

      <div className="docpro_admin_layout">
        <div>
          <div className='docpro_card'>
            <Spacing lg="8" sm="8" />
            <div className="docpro_funfact_1">
              <div className="docpro_funfact_item">
                <p>Your Available Letter Credit</p>
                <div className='flex items-center'>
                  <h3>{letterLimit ? letterLimit.available_credit : 0}</h3>
                  {informations.available_credit > 0 ? 
                    <>
                      <span className='px-3'>+</span> <h3 style={{ color:"#057f05" }}>{informations.available_credit}</h3>
                      <span className='px-3'>=</span> <h3>{letterLimit ? letterLimit.available_credit + informations.available_credit : informations.available_credit}</h3>
                    </> 
                    : ""
                  }
                </div>
              </div>
            </div>

            <div className="docpro_credit_wrap">
              <form onSubmit={handleSubmit}>
                <div className="docpro_credit_left">
                  <h2 className='docpro_fs_14'>Letter Credit: $10 ({letterQuantiity} credits)</h2>
                  <p className='docpro_mb_0'>How many credits would you like to buy? </p>
                  <Spacing lg="8" sm="8" />
                  <div className="docpro_quantity">
                    <button type="button" onClick={substrateCredit} disabled={informations.available_credit === 0}>
                      <svg width={16} height={4} viewBox="0 0 16 4" fill="none" xmlns="http://www.w3.org/2000/svg" >
                        <path d="M2.16602 2H13.8327" stroke="currentColor" strokeWidth={4} strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </button>
                    <span>{ informations? informations.available_credit : 0 }</span>
                    <button type="button" onClick={addMoreCredit}>
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" >
                        <path d="M7.99935 2.16602V13.8327M2.16602 7.99935H13.8327" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </button>
                  </div>
                  {can_visit("settings_letter_credits") && (
                    <button
                      type="submit"
                      className={`docpro_bordered_btn docpro_fw_600 size_lg docpro_filed_btn ${informations.available_credit === 0 ? "docpro_disabled" : ""}`}
                      style={{ pointerEvents: `${loading ? "none" : "initial"}` }}
                      disabled={informations.available_credit === 0}
                    >
                      <ShoppingCart width={20} height={20} />
                      Buy Credit Now
                      {loading && (
                        <span className="docpro_loading">
                          <Loader width={20} height={20} />
                        </span>
                      )}
                    </button>
                  )}
                </div>
              </form>
            </div>
            <Spacing lg="15" sm="15" />
          </div>
        </div>

        <div className='docpro_credit_wrap'>
          <div className="docpro_credit_right">
            <div className='docpro_card'>
              <Spacing lg="8" sm="8" />
              <h2 className='docpro_fs_20'>Order Summary</h2>
              <div className="docpro_table_1 hipaa_table" style={{ boxShadow: 'none', borderRadius: '5px' }}>
                <table className="docpro_mb_0" style={{ width: '100%' }}>
                  <tbody>
                    <tr>
                      <th className='py-2'>
                        <b>Description</b>
                      </th>
                      <th className="text-right py-2" style={{ width: '200px' }}>
                        <b>Qty</b>
                      </th>
                      <th className="text-right py-2" style={{ width: '200px' }}>
                        <b>Amount</b>
                      </th>
                    </tr>
                    <tr>
                      <td className='py-2'>DocPro Bulk Letter</td>
                      <td className="text-right py-2">{informations?.quantity}</td>
                      <td className="text-right py-2">
                        {informations?.available_credit} credits
                      </td>
                    </tr>
                    <tr>
                      <td className="text-right py-2" colSpan={2}>
                        <b>Sub Total</b>
                      </td>
                      <td className="text-right py-2">
                        <b>${totalCost?.toFixed(2)}</b>
                      </td>
                    </tr>
                    <tr>
                      <td className="text-right py-2" colSpan={2}>
                        <b>Discount</b>
                      </td>
                      <td className="text-right py-2">-$0.00</td>
                    </tr>
                    <tr>
                      <td className="text-right py-2" colSpan={2}>
                        <b className='docpro_fs_18 docpro_red_color'>Total</b>
                      </td>
                      <td className="text-right py-2">
                        <b className='docpro_fs_18 docpro_red_color'>${totalCost?.toFixed(2)}</b>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <Spacing lg="15" sm="15" />
            </div>
          </div>
        </div>
      </div>

      {paymentStatus && (
        <StatusViewModal
          openModal={paymentStatus}
          setOpenModal={setPaymentStatus}
          session={subscriptionSession}
          account_id={account_id}
          payment_type={subscriptionPaymentStatus}
          handleFormView={handleFormView}
          previousScrollPosition={previousScrollPosition}
        />
      )}

      {formView && (
        <FormViewDetailsModal
          formId = {formViewId}
          formSize = {'A4'}
          formView = {formView}
          setFormView = {setFormView}
          formType = {'invoice'}
          previousScrollPosition={previousScrollPosition}
        />
      )}
    </>
  );
}